import type { Role, TenantOption } from '@pages/Users/UserManagement/types'
import {
  Button,
  Drawer,
  Form,
  FormInstance,
  Input,
  Radio,
  Select,
  Space,
} from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { formatRoleName } from '../utils'

export type CreateUserDrawerProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  form: FormInstance
  availableRoles: Role[] | undefined
  isLoading: boolean
  tenantOptions: TenantOption[]
}

export function CreateUserDrawer({
  isOpen,
  onClose,
  onSubmit,
  form,
  availableRoles,
  isLoading,
  tenantOptions,
}: CreateUserDrawerProps) {
  const tenantChoices = tenantOptions.filter(
    (tenant) => tenant.name !== 'Gravitate',
  )

  const access_type = useWatch(['access_type'], form)
  const source = useWatch(['source'], form)

  return (
    <Drawer
      title='Create User'
      open={isOpen}
      onClose={onClose}
      width={600}
      destroyOnHidden
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='primary' onClick={onSubmit} loading={isLoading}>
            Create User
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout='vertical'
        className='mt-4'
        initialValues={{
          access_type: 'tenant',
          source: 'Basic',
          tenant_name: undefined,
          role: availableRoles?.[0]?._id,
        }}
      >
        <Form.Item
          name='email'
          label='Email'
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Invalid email format' },
          ]}
        >
          <Input placeholder='Enter email address' />
        </Form.Item>

        <Form.Item name='name' label='Full Name'>
          <Input placeholder='Enter full name' />
        </Form.Item>

        <Form.Item
          name='source'
          label='Source'
          rules={[{ required: true, message: 'Source is required' }]}
        >
          <Radio.Group optionType='button' buttonStyle='solid'>
            <Radio.Button value='Basic'>Basic</Radio.Button>
            <Radio.Button value='Google'>Google</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {source === 'Basic' && (
          <Form.Item
            name='password'
            label='Password'
            rules={[
              { required: true, message: 'Password is required' },
              {
                min: 8,
                message: 'Password must be at least 8 characters',
              },
            ]}
          >
            <Input.Password placeholder='Enter password' />
          </Form.Item>
        )}

        <Form.Item
          name='access_type'
          label='Access Type'
          rules={[{ required: true, message: 'Select an access type' }]}
        >
          <Radio.Group optionType='button' buttonStyle='solid'>
            <Radio.Button value='gravitate'>Gravitate</Radio.Button>
            <Radio.Button value='tenant'>Single Tenant</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {access_type === 'tenant' && (
          <Form.Item shouldUpdate>
            <Form.Item
              name='selected_tenant'
              label='Tenant'
              rules={[{ required: true, message: 'Select a tenant' }]}
            >
              <Select
                placeholder='Select tenant'
                showSearch
                optionFilterProp='label'
                options={tenantChoices.map((tenant) => ({
                  label: tenant.name,
                  value: tenant.name,
                }))}
              />
            </Form.Item>
          </Form.Item>
        )}

        <Form.Item
          name='selected_role_id'
          label='Role'
          rules={[{ required: true, message: 'Role is required' }]}
        >
          <Select
            placeholder='Select role'
            options={availableRoles?.map((role) => ({
              label: formatRoleName(role.name),
              value: role._id,
            }))}
            disabled={availableRoles?.length === 0}
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
